









































































































import Vue, { PropType } from "vue";
import { InvoicingMethod, ListItem, Nullable, PartialInvoicingDate, Project } from "@/types";
import DatePicker from "@/components/date-picker";
import InputMoney from "@/components/inputs/InputMoney.vue";
import { formatEuro, toAmount } from "@/util/money";

type Rule = (value: string) => true | string;

// Client-only row identity, kept beside the termijnen (never inside them, so the PATCH body is untouched).
let nextRowKey = 0;
const newRowKey = (): number => ++nextRowKey;

export default Vue.extend({
  components: {
    DatePicker,
    InputMoney
  },

  props: {
    value: {
      type: Object as PropType<Project>
    }
  },

  data() {
    const termijnen = this.value?.partialInvoicingDates;

    // What the CRM holds now: Per woning deletes the termijnen and clears the project value on save,
    // which the warning names before the user saves.
    return {
      rowKeys: (Array.isArray(termijnen) ? termijnen.map(newRowKey) : []) as number[],
      loadedMethod: (this.value?.invoicingMethod ?? null) as Nullable<InvoicingMethod>,
      loadedHasProjectAmounts:
        (Array.isArray(termijnen) && termijnen.length > 0) || toAmount(this.value?.projectValue) !== null
    };
  },

  computed: {
    invoicingMethods(): ListItem[] {
      return (this.$t("office.dictionaries.invoicingMethod") as unknown) as ListItem[];
    },

    termijnen(): PartialInvoicingDate[] {
      const termijnen = this.value.partialInvoicingDates;

      return Array.isArray(termijnen) ? termijnen : [];
    },

    dropsProjectAmounts(): boolean {
      return (
        this.value.invoicingMethod === "house_level" &&
        this.loadedMethod !== "house_level" &&
        this.loadedHasProjectAmounts
      );
    },

    remainder(): Nullable<number> {
      const projectValue = toAmount(this.value.projectValue);

      if (projectValue === null) return null;

      const allocated = this.termijnen.reduce((sum, termijn) => sum + (toAmount(termijn.invoiceValue) ?? 0), 0);

      return Math.round((projectValue - allocated) * 100) / 100;
    },

    remainderText(): string {
      if (this.remainder === null) return "";

      return this.$t("office.labels.hints.remainingToInvoice", [formatEuro(this.remainder)]).toString();
    },

    dateRules(): Rule[] {
      return [(value: string) => !!value || this.$t("validation.required").toString()];
    },

    amountRules(): Rule[] {
      return [(text: string) => (text || "").trim() !== "" || this.$t("validation.required").toString()];
    }
  },

  methods: {
    replaceTermijnen(termijnen: PartialInvoicingDate[]): void {
      this.$set(this.value, "partialInvoicingDates", termijnen);
    },

    addTermijn(): void {
      this.rowKeys = [...this.rowKeys, newRowKey()];
      this.replaceTermijnen([...this.termijnen, { invoiceDate: null, invoiceValue: null }]);
    },

    removeTermijn(index: number): void {
      this.rowKeys = this.rowKeys.filter((_, i) => i !== index);
      this.replaceTermijnen(this.termijnen.filter((_, i) => i !== index));
    },

    setTermijnDate(index: number, invoiceDate: Nullable<string>): void {
      this.replaceTermijnen(this.termijnen.map((termijn, i) => (i === index ? { ...termijn, invoiceDate } : termijn)));
    },

    setTermijnValue(index: number, invoiceValue: Nullable<number>): void {
      this.replaceTermijnen(this.termijnen.map((termijn, i) => (i === index ? { ...termijn, invoiceValue } : termijn)));
    }
  }
});
